import { useRef, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import download from 'downloadjs';

import { StatusBar } from '@hotjar/webapp/features/Heatmaps/components/StatusBar/StatusBar';
import { StatusBar as StatusBarLegacy } from '@hotjar/webapp/features/Heatmaps/components/StatusBar/StatusBarLegacy';
import {
  Creator as HighlightCreatorContainer,
  useHighlightsState,
} from '@hotjar/webapp/features/Highlights/Heatmaps';
import { DATE_RANGE_TYPE, convertFromNewUiToQueryFormat } from '@hotjar/global-filters';
import { CONTINUOUS_TYPES } from '@hotjar/webapp/features/Heatmaps/components/StatusBar/constants';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { MessageContainer } from '@hotjar/webapp/features/Heatmaps/components/EmptyStates/MessageContainer';
import { EnterUrlMessage } from '@hotjar/webapp/features/Heatmaps/components/EmptyStates/EnterUrlMessage';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { ERROR_REPORTING } from '@hotjar/recordings/src/constants/feature-flags';
import { isOnActiveTrial } from '@hotjar/subscription-management';
import { useAccountTrialInfo } from '@hotjar/account';
import { useCurrentSite } from '@hotjar/site';
import { EVENT_UI_POSITIONS, EVENT_NAMES } from '@hotjar/webapp/domains/heatmaps/tracking';
import { getDownloadFilename } from '@hotjar/webapp/features/Heatmaps/utils/getDownloadName';
import {
  GeneralSurvey,
  HOTJAR_EVENTS,
  SMART_PANEL,
  SmartPanel,
  isSinglePageSearch as isSinglePage,
  MagicRuler,
  HEATMAP_DATA_TYPES,
  ScreenshotLayer,
  HeatLayer,
  getLayersImageData,
  EngagementLayer,
  getEventTriggeredOnState,
  useGetEngagementData,
  SMART_PANEL_LAYERS_SECTION,
  EngagementMarkersLayer,
} from '@hotjar/heatmaps';
import type { CanvasRef } from '@hotjar/heatmaps';
import { useFeatureFlag } from '@hotjar/feature-flags';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import type { ReportProps } from './ReportTypes';
import {
  renderClickRegions,
  renderScrollMarkers,
  renderErrorMessage,
  ReportContainer,
  LayersContainer,
  renderTopClicks,
  MagicRulerContainer,
  MagicRulerAndLayersContainer,
} from './renderers';
// TEMP until after the 22nd of April.
import { useMoveDataUnavailable } from '../../hooks/useMoveDataUnavailable';

const triggerHotjarEvent = (eventName: string) => {
  if (window.hj) {
    window.hj('event', eventName);
  }
};

const SurveyContainer = styled.div`
  position: relative;
`;

export const Report = ({
  errorType,
  heatmapData,
  modelState,
  notInstalled,
  onDownloadData,
  onRetakeScreenshotClicked,
  onRetry,
  onNavigateToHeatmap,
  onUpgradeCoverage,
  screenshotData,
  selectedDateRange,
  selectedDeviceType,
  selectedHeatType,
  selectedTargetingValue,
  siteId,
  source,
  shouldOpenManualRetakerOnError,
  selectedFiltersInUiFormat,
  clickCount,
  effectivePlan,
}: ReportProps): JSX.Element => {
  const { t } = useTranslation('heatmaps');

  const isFetching = notInstalled === false && modelState === 'FETCHING';
  const isError = notInstalled === false && modelState === 'FETCH_ERROR';
  const isDoneFetching = notInstalled === false && modelState === 'DATA_FETCHED';
  const isInit = notInstalled === false && modelState === 'INIT';

  const dataFetched = isDoneFetching && screenshotData && heatmapData;

  const isSmartPanelEnabled = useFeatureFlag(SMART_PANEL);
  const [isSmartPanelOpen, setSmartPanelOpen] = useState(true);
  const toggleSmartPanel = () => {
    setSmartPanelOpen((isCurrentlyOpen) => !isCurrentlyOpen);
  };

  const screenshotCanvasRef = useRef<CanvasRef>(null);
  const { data: currentSite } = useCurrentSite();
  const filters = useMemo(
    () => ({
      device: selectedDeviceType,
      match: selectedTargetingValue,
      type: selectedHeatType,
      url: source,
    }),
    [selectedDeviceType, selectedHeatType, selectedTargetingValue, source],
  );
  const highlightsState = useHighlightsState(filters);

  const isSinglePageSearch = isSinglePage(selectedTargetingValue);

  const track = useTrack();

  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   * Check if error reporting feature
   */
  const trialInfo = useAccountTrialInfo();
  const planAllowsErrorCapturing =
    trialInfo &&
    currentSite &&
    isSiteOnScaleOrScaleTrial({
      ...currentSite,
      trial: { active: isOnActiveTrial(trialInfo) },
    });
  const isErrorReportingFFEnabled = useFeatureFlag(ERROR_REPORTING);

  const onDownloadImage = () => {
    const imageData = getLayersImageData();

    if (!imageData || !currentSite) {
      return;
    }

    download(
      imageData,
      getDownloadFilename({
        ext: 'jpg',
        siteId: siteId.toString(),
        type: selectedHeatType,
        device: selectedDeviceType,
      }),
    );

    track.download(
      capitalize(['Heatmap', selectedHeatType, 'Image'].join(' ')),
      'jpg',
      track.getGroupDataForSite(currentSite),
      getEventTriggeredOnState(),
    );
  };

  const trackClick = (eventName: string) => {
    if (currentSite) {
      track.click(
        eventName,
        EVENT_UI_POSITIONS.CC_HEATMAPS,
        track.getGroupDataForSite(currentSite),
        getEventTriggeredOnState(),
      );
    }
  };

  const handleThumbsUpClick = () => {
    trackClick(EVENT_NAMES.HEATMAP_ACCURATE);
    triggerHotjarEvent(HOTJAR_EVENTS.HEATMAP_ACCURATE);
  };

  const handleThumbsDownClick = () => {
    trackClick(EVENT_NAMES.HEATMAP_INACCURATE);
    triggerHotjarEvent(HOTJAR_EVENTS.HEATMAP_INACCURATE);
  };

  const [isTopClicksWidgetActive, setTopClicksWidgetActive] = useState(true);
  const [isHeatLayerActive, setHeatLayerActive] = useState(true);
  const shouldShowHeatLayer = selectedHeatType !== HEATMAP_DATA_TYPES.CLICK || isHeatLayerActive;
  const [isEngagementZonesActive, setEngagementZonesActive] = useState(false);

  const handleTopClicksWidgetToggle = (isActive: boolean) => {
    setTopClicksWidgetActive(isActive);

    if (!isHeatLayerActive) {
      setHeatLayerActive(true);
    }
  };

  const handleHeatLayerToggle = (isActive: boolean) => {
    setHeatLayerActive(isActive);
    setTopClicksWidgetActive(isActive);
  };

  const handleEngagementZonesToggle = (isActive: boolean) => {
    setEngagementZonesActive(isActive);
  };

  const trackSurveyViewed = () => {
    if (currentSite) {
      track.viewElement(
        EVENT_NAMES.GENERAL_SURVEY,
        EVENT_UI_POSITIONS.CC_HEATMAPS,
        track.getGroupDataForSite(currentSite),
        getEventTriggeredOnState(),
      );
    }
  };

  // TEMP until after the 22nd of April.
  useMoveDataUnavailable(selectedDateRange, selectedHeatType);

  const isHeatmapImageDataAvailable = Boolean(
    screenshotData?.image &&
      screenshotData?.imageSize &&
      heatmapData?.image &&
      heatmapData?.imageSize,
  );

  const isEngagementZonesAvailable =
    useFeatureFlag(SMART_PANEL_LAYERS_SECTION) &&
    effectivePlan !== null &&
    effectivePlan !== 'basic' &&
    effectivePlan !== 'plus';

  const { error } = useNotify();
  const onEngagementZonesDataError = useCallback(() => {
    error(t('smart-panel.engagement-zones.request-error'));
  }, [error, t]);

  const params = useMemo(
    () => ({
      siteId: currentSite?.id || 0,
      deviceType: selectedDeviceType,
      url: source,
      matchOperation: selectedTargetingValue,
      jsonQueryString: JSON.stringify(convertFromNewUiToQueryFormat(selectedFiltersInUiFormat)),
      enabled: isEngagementZonesAvailable && isHeatmapImageDataAvailable && isEngagementZonesActive,
      screenshotId: screenshotData?.screenshotId || null,
      onEngagementZonesDataError,
    }),
    [
      currentSite?.id,
      screenshotData?.screenshotId,
      selectedDeviceType,
      selectedFiltersInUiFormat,
      selectedTargetingValue,
      source,
      isEngagementZonesAvailable,
      isHeatmapImageDataAvailable,
      isEngagementZonesActive,
      onEngagementZonesDataError,
    ],
  );

  const engagementData = useGetEngagementData(params);
  const hasEngagementData =
    !engagementData?.isError &&
    !engagementData?.isLoading &&
    engagementData?.data?.engagementGrid &&
    engagementData.data.engagementGrid.length > 0;

  const shouldNotDisableNonEngagementInteractions =
    !isEngagementZonesAvailable || !isEngagementZonesActive;

  return (
    <ReportContainer>
      {isInit && (
        <MessageContainer>
          <EnterUrlMessage />
        </MessageContainer>
      )}

      {isFetching && (
        <MessageContainer data-testid="hm-report-loading">
          <EmptyState
            loading
            title={t('loading-state.title')}
            subtitle={t('loading-state.subtitle')}
            kind="condensed"
          />
        </MessageContainer>
      )}

      {isError && (
        <MessageContainer>
          {renderErrorMessage({
            siteId,
            errorType,
            onRetry,
            onNavigateToHeatmap,
            onUpgradeCoverage,
            shouldOpenManualRetaker: shouldOpenManualRetakerOnError,
          })}
        </MessageContainer>
      )}

      {dataFetched && (
        <>
          {screenshotData.recordingCreated && (
            <>
              {isSmartPanelEnabled ? (
                <StatusBar
                  highlights={highlightsState}
                  onDownloadImage={onDownloadImage}
                  onDownloadData={onDownloadData}
                  currentSite={currentSite}
                  filtersApplied={selectedFiltersInUiFormat.filterList.length > 0}
                />
              ) : (
                <StatusBarLegacy
                  captureType={CONTINUOUS_TYPES.CONTINUOUS}
                  // viewsCount is mapped to sessions count. Will unify the terminology once MH code is deleted
                  lastScreenshotTimestamp={screenshotData.recordingCreated}
                  isSelectedDateRangeAbsolute={selectedDateRange.type === DATE_RANGE_TYPE.ABSOLUTE}
                  onRetakeScreenshotClicked={onRetakeScreenshotClicked}
                  highlights={highlightsState}
                  onDownloadImage={onDownloadImage}
                  onDownloadData={onDownloadData}
                  selectedDeviceType={selectedDeviceType}
                  isSinglePageSearch={isSinglePageSearch}
                  screenshotData={screenshotData}
                  currentSite={currentSite}
                  filtersApplied={selectedFiltersInUiFormat.filterList.length > 0}
                />
              )}
            </>
          )}
          {screenshotData.image &&
            screenshotData.imageSize &&
            heatmapData.image &&
            heatmapData.imageSize && (
              <MagicRulerAndLayersContainer>
                <MagicRulerContainer>
                  <MagicRuler
                    imageHeight={heatmapData.imageSize[1]}
                    selectedFiltersInUiFormat={selectedFiltersInUiFormat}
                    siteId={siteId}
                    selectedTargetingValue={selectedTargetingValue}
                    searchPattern={source}
                    selectedDeviceType={selectedDeviceType}
                    isDisabled={selectedHeatType === HEATMAP_DATA_TYPES.SCROLL}
                    currentSite={currentSite}
                  />
                </MagicRulerContainer>
                <LayersContainer
                  fullWidth={false}
                  isSmartPanelEnabled={isSmartPanelEnabled}
                  isSmartPanelOpen={isSmartPanelOpen}
                >
                  <ScreenshotLayer
                    screenshotImageData={screenshotData.image}
                    width={screenshotData.imageSize[0]}
                    height={screenshotData.imageSize[1]}
                    ref={screenshotCanvasRef}
                  />
                  <HeatLayer
                    heatmapImageData={heatmapData.image}
                    width={heatmapData.imageSize[0]}
                    height={heatmapData.imageSize[1]}
                    heatmapType={selectedHeatType}
                    shouldShowHeatLayer={shouldShowHeatLayer}
                  />

                  {shouldNotDisableNonEngagementInteractions &&
                    renderClickRegions({
                      heatmapData,
                      screenshotData,
                      selectedDeviceType,
                      onRegionClick: highlightsState.highlightRegion,
                    })}
                  {isTopClicksWidgetActive &&
                    renderTopClicks(heatmapData, screenshotData, selectedDeviceType)}
                  {isEngagementZonesAvailable && (
                    <>
                      <EngagementLayer
                        width={heatmapData.imageSize[0]}
                        height={heatmapData.imageSize[1]}
                        engagementZones={hasEngagementData ? engagementData?.data : undefined}
                        isEngagementZonesActive={isEngagementZonesActive}
                        heatmapType={selectedHeatType}
                        deviceType={selectedDeviceType}
                      />
                      <EngagementMarkersLayer
                        selectedDeviceType={selectedDeviceType}
                        engagementZones={hasEngagementData ? engagementData?.data : undefined}
                        isEngagementZonesActive={isEngagementZonesActive}
                      />
                    </>
                  )}
                  {shouldNotDisableNonEngagementInteractions && renderScrollMarkers(heatmapData)}
                  <HighlightCreatorContainer
                    width={screenshotData.imageSize[0]}
                    height={screenshotData.imageSize[1]}
                    screenshotSourceUrl={screenshotData.originUrl ?? ''}
                    filters={filters}
                    highlightsState={highlightsState}
                    canvasRef={screenshotCanvasRef}
                  />
                </LayersContainer>
              </MagicRulerAndLayersContainer>
            )}
          {isSmartPanelEnabled && (
            <SmartPanel
              currentSite={currentSite}
              isOpen={isSmartPanelOpen}
              onRetakeScreenshotClicked={onRetakeScreenshotClicked}
              isTopClicksWidgetActive={isTopClicksWidgetActive}
              onTopClicksWidgetToggle={handleTopClicksWidgetToggle}
              isHeatLayerActive={isHeatLayerActive}
              onHeatLayerToggle={handleHeatLayerToggle}
              originUrl={screenshotData?.originUrl}
              toggle={toggleSmartPanel}
              selectedDeviceType={selectedDeviceType}
              clickCount={clickCount}
              screenshotTimestamp={screenshotData.recordingCreated}
              selectedHeatType={selectedHeatType}
              selectedDateRange={selectedDateRange}
              selectedTargetingValue={selectedTargetingValue}
              searchPattern={source}
              selectedFiltersInUiFormat={selectedFiltersInUiFormat}
              isErrorReportingFFEnabled={isErrorReportingFFEnabled}
              planAllowsErrorCapturing={Boolean(planAllowsErrorCapturing)}
              highlights={highlightsState}
              isEngagementZonesActive={isEngagementZonesActive}
              onEngagementZonesToggle={handleEngagementZonesToggle}
              isEngagementZonesAvailable={isEngagementZonesAvailable}
              isFetchingEngagementData={engagementData?.isFetching ?? false}
            />
          )}
          <SurveyContainer>
            <GeneralSurvey
              onThumbsUpClick={handleThumbsUpClick}
              onThumbsDownClick={handleThumbsDownClick}
              onSurveyVisible={trackSurveyViewed}
            />
          </SurveyContainer>
        </>
      )}
    </ReportContainer>
  );
};
